import { apiRequest } from "../api/client";
import { KIND_HREFS } from "../lib/entityHrefs";
import { frameworkPages } from "./frameworkMeta";
import type { FrameworkId } from "../context/FrameworksContext";
import type { PaletteEntry } from "./types";

// ── Record search: the organisation's own rows in the palette ────────────
// GET /search (backend routes/search.routes.ts) answers with rows the caller
// could already open from each register, each repeating that register's read
// guard; this module turns them into palette rows that land on the record
// (the same deep links the cross-reference panels use, components/EntityTags.tsx).

export const RECORD_SEARCH_MIN = 2;

type RecordKind = "control" | "risk" | "incident" | "supplier" | "asset" | "document" | "action" | "template";

interface RecordResult {
  kind: RecordKind;
  id: string;
  label: string;
  hint?: string;
  frameworkId?: string;
  controlKind?: string;
}

const TAGS: Record<RecordKind, string> = {
  control: "Control", risk: "Risk", incident: "Incident", supplier: "Supplier", asset: "Asset",
  document: "Evidence", action: "Action", template: "Policy",
};

/**
 * The page a control is worked on. ISO 27001 and ISO 42001 keep clauses and
 * annex controls on separate pages; ISO 9001 / 45001 / 14001 work their
 * clauses on the Statement of Applicability; everything else on its first page.
 */
function controlPage(frameworkId: string, controlKind: string | undefined): string | null {
  const clause = controlKind === "CLAUSE";
  switch (frameworkId) {
    case "iso27001": return clause ? "/mandatory-clauses" : "/annex-a";
    case "iso42001": return clause ? "/iso42001" : "/iso42002";
    case "iso9001": return "/iso9001-soa";
    case "iso45001": return "/iso45001-soa";
    case "iso14001": return "/iso14001-soa";
    case "pci_dss": return "/pci-dss";
    default: return frameworkPages(frameworkId as FrameworkId)[0]?.to ?? null;
  }
}

/** Shell-relative path of a result; ?control= puts the control list's search on it. */
function hrefOf(r: RecordResult): string | null {
  if (r.kind === "control") {
    const page = r.frameworkId ? controlPage(r.frameworkId, r.controlKind) : null;
    return page ? `${page}?control=${encodeURIComponent(r.id)}` : null;
  }
  if (r.kind === "template") return `/templates?template=${encodeURIComponent(r.id)}`;
  return KIND_HREFS[r.kind](r.id);
}

/**
 * Rows for a query, with shell-relative paths (the shell resolves them through
 * its LinkAdapter). A failure answers no rows: the pages still match.
 */
export async function searchRecords(query: string, signal: AbortSignal): Promise<PaletteEntry[]> {
  const q = query.trim();
  if (q.length < RECORD_SEARCH_MIN) return [];
  const data = await apiRequest<{ results: RecordResult[] }>("/search", { method: "GET", query: { q }, signal });
  const out: PaletteEntry[] = [];
  for (const r of data.results) {
    const to = hrefOf(r);
    if (!to) continue;
    out.push({ id: `record:${r.kind}:${r.id}`, kind: "record", label: r.label, hint: r.hint, tag: TAGS[r.kind], to });
  }
  return out;
}
